import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, Video, UserPlus, Gavel, Save, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const RANGES = [[7, "7 days"], [30, "30 days"], [90, "90 days"], [0, "All time"]];

function FunnelStat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-border bg-muted/40 p-4" data-testid={testid}>
      <Icon className="h-4 w-4 text-kinetic mb-2" />
      <p className="text-2xl font-display font-extrabold leading-none">{value}</p>
      <p className="text-xs uppercase tracking-wider text-muted-foreground mt-1">{label}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

function InventoryStats() {
  const [days, setDays] = useState(30);
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/inventory-stats", { params: { days } }).then((r) => setS(r.data)).catch(() => setS(null)); }, [days]);
  const v = (k) => (s ? s[k] : "—");
  return (
    <Card className="bg-card">
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
        <div>
          <CardTitle className="text-lg">Free inventory tool</CardTitle>
          <p className="text-sm text-muted-foreground">Logged-out visitors who used <a href="/inventory" target="_blank" rel="noreferrer" className="underline hover:text-kinetic inline-flex items-center gap-0.5">/inventory <ExternalLink className="h-3 w-3" /></a> and how many became customers.</p>
        </div>
        <div className="flex gap-1 rounded-lg border border-border p-1" data-testid="inv-stats-range">
          {RANGES.map(([d, l]) => (
            <button key={d} type="button" onClick={() => setDays(d)} data-testid={`inv-stats-range-${d}`}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${days === d ? "bg-asphalt text-white" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <FunnelStat icon={Users} label="Visitors used tool" value={v("visitors")} sub={s ? `${s.items} items typed in` : null} testid="inv-stat-visitors" />
          <FunnelStat icon={Video} label="AI room videos" value={v("videos")} sub={s ? `${s.video_visitors} visitors · ≈US$${s.est_ai_cost_usd} AI cost` : null} testid="inv-stat-videos" />
          <FunnelStat icon={UserPlus} label="Signed up" value={v("signed_up")} sub={s ? `${s.signup_rate}% of visitors` : null} testid="inv-stat-signed-up" />
          <FunnelStat icon={Gavel} label="Posted a move" value={v("posted")} sub={s ? `${s.post_rate}% of visitors` : null} testid="inv-stat-posted" />
        </div>
        <p className="text-xs text-muted-foreground">A visitor counts once they add an item or a room video. "Signed up" means they created an account and carried their inventory into a job post. AI cost is an estimate (about 2 US cents per video).</p>
      </CardContent>
    </Card>
  );
}

function GuestLimits() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = useCallback(() => { api.get("/admin/guest-limits").then((r) => setF({ videos: String(r.data.videos_per_day), mb: String(r.data.upload_mb_per_day) })).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/guest-limits", { videos_per_day: parseInt(f.videos, 10), upload_mb_per_day: parseInt(f.mb, 10) });
      toast.success("Free tool limits updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <Card className="bg-card">
      <CardHeader>
        <CardTitle className="text-lg">Daily limits for logged-out visitors</CardTitle>
        <p className="text-sm text-muted-foreground">Per internet connection (IP address), reset at midnight UTC. Logged-in customers are never limited.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="lim-videos">AI room videos per day</Label>
            <Input id="lim-videos" type="number" min="0" max="500" value={f?.videos ?? ""} disabled={!f} onChange={(e) => setF({ ...f, videos: e.target.value })} data-testid="guest-limit-videos-input" className="w-32" />
            <p className="text-xs text-muted-foreground">0 pauses AI video for visitors. They can still type items in.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lim-mb">Video upload per day (MB)</Label>
            <Input id="lim-mb" type="number" min="100" max="20000" step="100" value={f?.mb ?? ""} disabled={!f} onChange={(e) => setF({ ...f, mb: e.target.value })} data-testid="guest-limit-mb-input" className="w-32" />
            <p className="text-xs text-muted-foreground">100–20,000 MB. A phone room video is usually 20–100 MB.</p>
          </div>
        </div>
        <Button onClick={save} disabled={!f || saving} className="bg-kinetic hover:bg-kinetic-hover text-white gap-2" data-testid="guest-limits-save-btn"><Save className="h-4 w-4" /> Save limits</Button>
      </CardContent>
    </Card>
  );
}

export function FreeToolTab() {
  return (
    <div className="space-y-6" data-testid="free-tool-tab">
      <InventoryStats />
      <GuestLimits />
    </div>
  );
}
